import { cn } from "@/lib/utils";
import type { CurrentCycle, CycleEvent, CycleEventType } from "@payrollmart-invoice/shared";

/**
 * Map from an event type to the Tailwind arbitrary-color string used for the
 * 3px bottom bar inside each day cell. Kept in one place so the legend in the
 * card header and the cell bars never drift out of sync.
 *
 * Colors come from the handoff: upload→primary indigo, extract→muted green,
 * review→warning, push→success, milestone→ink, deadline→error.
 */
export const CYCLE_EVENT_COLOR: Record<CycleEventType, string> = {
	upload: "#4f46e5",
	extract: "#7bb3a8",
	review: "#d97706",
	push: "#16a34a",
	milestone: "#1e1e20",
	deadline: "#dc2626",
};

const CYCLE_EVENT_LABEL: Record<CycleEventType, string> = {
	upload: "Upload",
	extract: "Extract",
	review: "Review",
	push: "Push",
	milestone: "Milestone",
	deadline: "Close",
};

const DOW_HEADERS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;
/** Stable keys for the leading blank cells (up to 6 — Sunday has zero). */
const EMPTY_CELL_KEYS = ["lead-0", "lead-1", "lead-2", "lead-3", "lead-4", "lead-5"] as const;
const MONTH_ABBR = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
] as const;

interface CycleCalendarProps {
	cycle: CurrentCycle;
}

/**
 * Month-at-a-glance calendar for the billing cycle. Renders a 7-column grid
 * with leading empty cells for the weekday offset, then one aspect-square
 * cell per day in the cycle. Today is ringed in indigo; past days with no
 * event are dimmed; days with an event carry a colored bar + short label.
 *
 * The component is purely presentational — `cycle` is the server-assembled
 * payload from `trpc.invoices.currentCycle`.
 */
export function CycleCalendar({ cycle }: CycleCalendarProps) {
	const openDate = new Date(`${cycle.openDate}T00:00:00.000Z`);
	const firstDow = openDate.getUTCDay();
	const monthIndex = openDate.getUTCMonth();
	const monthAbbr = MONTH_ABBR[monthIndex] ?? "";

	const eventsByDay = new Map<number, CycleEvent>();
	for (const ev of cycle.events) eventsByDay.set(ev.day, ev);

	const todayDay = cycle.dayOfCycle;
	const totalDays = cycle.totalDays;

	// Unique event types actually present in this cycle — drives the legend.
	const legendTypes: CycleEventType[] = [];
	const seenTypes = new Set<CycleEventType>();
	for (const ev of cycle.events) {
		if (!seenTypes.has(ev.type)) {
			seenTypes.add(ev.type);
			legendTypes.push(ev.type);
		}
	}

	return (
		<div className="rounded-lg border border-[#e5e7eb] bg-white" data-testid="cycle-calendar">
			<div className="flex items-center justify-between border-b border-[#e5e7eb] px-6 py-4">
				<h2 className="text-sm font-semibold text-[#1e1e20]">{cycle.monthLabel} at a glance</h2>
				<div className="flex flex-wrap items-center gap-3 text-[11px]">
					{legendTypes.map((t) => (
						<span key={t} className="flex items-center gap-1.5 text-[#6b7280]">
							<span
								className="h-2 w-2 rounded-sm"
								style={{ backgroundColor: CYCLE_EVENT_COLOR[t] }}
							/>
							{CYCLE_EVENT_LABEL[t]}
						</span>
					))}
				</div>
			</div>
			<div className="p-4">
				<div className="mb-1.5 grid grid-cols-7 gap-1">
					{DOW_HEADERS.map((d) => (
						<div
							key={d}
							className="text-center text-[10px] font-medium uppercase tracking-[0.08em] text-[#9ca3af]"
						>
							{d}
						</div>
					))}
				</div>
				<div className="grid grid-cols-7 gap-1" data-testid="cycle-calendar-grid">
					{EMPTY_CELL_KEYS.slice(0, firstDow).map((key) => (
						<div key={key} aria-hidden="true" />
					))}
					{Array.from({ length: totalDays }, (_, i) => {
						const day = i + 1;
						const event = eventsByDay.get(day);
						const isToday = day === todayDay;
						const isPast = day < todayDay;
						const dim = isPast && !event;
						return (
							<div
								key={day}
								data-testid="cycle-day-cell"
								data-day={day}
								data-event-type={event?.type}
								className={cn(
									"relative flex aspect-square flex-col rounded-md border p-1.5 transition-opacity",
									isToday ? "border-2 border-[#4f46e5] bg-white" : "border-[#e5e7eb] bg-white",
									dim && "opacity-60",
								)}
							>
								<div className="text-[10px] font-medium text-[#6b7280]">
									{monthAbbr} {day}
								</div>
								{event && (
									<div className="mt-auto">
										<div
											className="h-[3px] rounded-sm"
											style={{ backgroundColor: CYCLE_EVENT_COLOR[event.type] }}
										/>
										{event.label && (
											<div className="mt-1 truncate text-[9px] font-medium leading-tight text-[#1e1e20]">
												{event.label}
											</div>
										)}
									</div>
								)}
								{isToday && (
									<span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-[#4f46e5]" />
								)}
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}
